import { debounce } from "lodash";
import { useMemo, useState } from "react";
import { useNavigate } from "react-router";
import Actions from "../../components/Common/Actions";
import AntdBreadcrumbs from "../../components/Common/Breadcrumbs/AntdBreadcrumbs";
import FilledButton from "../../components/Common/Buttons/FilledButton";
import DoctorsFilterOptions from "../../components/Common/CommonFilterOptions";
import ListPageLayout from "../../components/Layout";

const AddNewRetailPharmacies = () => {
  const navigate = useNavigate();
  const addNewRetailPharmacies = {
    id: "addnewpost",
    onClick: () => navigate("/add-new-retail-pharmacy-form"),
    style: { width: "240px" },
    label: "Add New Retail Pharmacies",
  };
  const [search, setSearch] = useState("");
  const [sort, setSort] = useState("newest");
  const [limit, setLimit] = useState(10);
  const [currentPage, setCurrentPage] = useState(1);
  const [sortName, setSortName] = useState("Newest First");

  // Full Data
  const fullData = [
    {
      sno: "01",
      name: "Nalam Pharmacy",
      city: "Coimbatore",
      state: "Tamilnadu",
      pincode: "641 006",
      locality: "Ganapathy",
      license: "TNPLOU1619",
      mobile: "92165 61226",
      actions: "Activate",
    },
    {
      sno: "02",
      name: "Orbit Pharmacy",
      city: "Chennai",
      state: "Tamilnadu",
      pincode: "600016",
      locality: "Alwarpet",
      license: "TN002612607",
      mobile: "92165 61226",
      actions: "Track",
    },
    {
      sno: "03",
      name: "isue Pharmacy",
      city: "Madhurai",
      state: "Tamilnadu",
      pincode: "600 610",
      locality: "kambar Nagar",
      license: "TN00RD7570",
      mobile: "92165 61226",
      actions: "Track",
    },
    {
      sno: "04",
      name: "KMC Medical",
      city: "Trichy",
      state: "Tamilnadu",
      pincode: "641 060",
      locality: "Avinash Road",
      license: "TN00RD7570",
      mobile: "92165 61226",
      actions: "Track",
    },
  ];

  // SEARCH + SORT + PAGINATION
  const filteredSortedData = useMemo(() => {
    let temp = [...fullData];

    // Search
    if (search) {
      const lowerSearch = search.toLowerCase();
      temp = temp.filter((item) =>
        Object.values(item).some((val) =>
          String(val).toLowerCase().includes(lowerSearch),
        ),
      );
    }

    // Sort
    switch (sort) {
      case "newest":
        temp.sort((a, b) => b.sno.localeCompare(a.sno));
        break;
      case "oldest":
        temp.sort((a, b) => a.sno.localeCompare(b.sno));
        break;
      case "ascending":
        temp.sort((a, b) => a.name.localeCompare(b.name));
        break;
      case "descending":
        temp.sort((a, b) => b.name.localeCompare(a.name));
        break;
      default:
        break;
    }

    return temp;
  }, [search, sort]);

  const totalItems = filteredSortedData.length;
  const totalPages = Math.ceil(totalItems / limit);
  const paginatedData = useMemo(() => {
    const start = (currentPage - 1) * limit;
    const end = start + limit;
    return filteredSortedData.slice(start, end);
  }, [filteredSortedData, currentPage, limit]);

  const handleSortClick = (selectedSort) => {
    setSort(selectedSort);
    setSortName(
      {
        newest: "Newest First",
        oldest: "Oldest First",
        ascending: "Alphabetic A-Z",
        descending: "Alphabetic Z-A",
      }[selectedSort] || "Sort by",
    );
  };

  const debouncedSearch = debounce((value) => {
    setSearch(value);
    setCurrentPage(1);
  }, 500);

  const onChangeSearch = (value) => debouncedSearch(value);

  const handlePageChange = (page) => setCurrentPage(page);

  const columns = [
    {
      title: "S.No",
      render: (text, record, index) =>
        Number((currentPage - 1) * limit || 0) + (index + 1),
      key: "serial",
      width: 80,
      fixed: "left",
    },
    {
      title: "Name of Pharmacy",
      dataIndex: "name",
      key: "name",
      fixed: "left",
      width: 200,
    },
    {
      title: "City / Town",
      dataIndex: "city",
      key: "city",
      width: 150,
    },
    {
      title: "State",
      dataIndex: "state",
      key: "state",
      width: 250,
    },
    {
      title: "Pincode",
      dataIndex: "pincode",
      key: "pincode",
      width: 250,
    },
    {
      title: "Locality",
      dataIndex: "locality",
      key: "locality",
      width: 200,
    },
    {
      title: "Pharmacy License No",
      dataIndex: "license",
      key: "license",
      width: 250,
    },
    {
      title: "Contact Details (Mobile)",
      dataIndex: "mobile",
      key: "mobile",
      width: 250,
    },
    {
      title: "Actions",
      key: "actions",
      width: 250,
      render: (_, record) => (
        <Actions
          isEdit={true}
          isDelete={true}
          isView={true}
          onClickView={() => navigate(`/view-retail-pharmacy-form`)}
          onClickDelete={() => navigate("/edit-retail-pharmacy-form")}
          onClickEdit={() => navigate("/edit-retail-pharmacy-form")}
        />
      ),
    },
    {
      title: "Track Orders / Progress",
      key: "track",
      width: 180,
      render: (_, record) => (
        <span
          className="text-primarycolor mx-2 cursor-pointer"
          onClick={() =>
            record.actions === "Activate"
              ? navigate("/retail-pharmacy-active")
              : navigate("/retail-pharmacy-track")
          }
        >
          {record.actions}
        </span>
      ),
    },
  ];

  const breadcrumbsList = [
    { text: "Partnered Retail Pharmacies", link: "/add-new-retail-pharmacy" },
    { text: "List of Partnered Retail Pharmacies", link: "" },
  ];

  return (
    <ListPageLayout
      breadcrumbs={
        <AntdBreadcrumbs
          className="healthsyMasterBreadCrumbs"
          breadcrumbsList={breadcrumbsList}
        />
      }
      addButton={<FilledButton props={addNewRetailPharmacies} />}
      totalCount={totalItems}
      pagination={{
        currentPage,
        skip: (currentPage - 1) * limit,
        limit,
        totalPages,
        onPageChange: handlePageChange,
      }}
      filters={
        <DoctorsFilterOptions
          limit={limit}
          setLimit={setLimit}
          search={search}
          onChange={onChangeSearch}
          handleSortClick={handleSortClick}
          sortName={sortName}
          setFilterShow={() => {}}
          showFilter={false}
        />
      }
      tableProps={{
        columns,
        dataSource: paginatedData,
        className: "mx-3 pt-2 table-scrollable medicineList_table",
        pagination: false,
      }}
    />
  );
};

export default AddNewRetailPharmacies;
